import { useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useState } from 'react';
import { GRID_STYLES } from '@/core/components/ui/grid-styles';
import { cn } from '@/core/lib/utils';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/core/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/core/components/ui/dropdown-menu';
// Note: Using native HTML table with GRID_STYLES for consistent styling across the app
import {
  Plus,
  RefreshCw,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  FileText,
  XCircle,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  ManufacturerItem,
  ManufacturerStatus,
  ManufacturerStatusLabels,
} from '../../types/manufacturer-types';
import { exportManufacturersToExcel } from '../../utils/manufacturerExport';

// Format date for display
const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// Sortable column header component
const SortableHeader = ({ column, label }: { column: any; label: string }) => (
  <Button
    variant="ghost"
    onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    className="-ml-4"
  >
    {label}
    {column.getIsSorted() === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : column.getIsSorted() === 'desc' ? (
      <ArrowDown className="ml-2 h-4 w-4" />
    ) : (
      <ArrowUpDown className="ml-2 h-4 w-4" />
    )}
  </Button>
);

interface ManufacturerListProps {
  manufacturers: ManufacturerItem[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  onRefresh: () => void;
  onAdd: () => void;
  onEdit: (manufacturer: ManufacturerItem) => void;
  onDelete: (manufacturer: ManufacturerItem) => void;
  onClearError: () => void;
}

export function ManufacturerList({
  manufacturers,
  isLoading,
  error,
  totalCount,
  onRefresh,
  onAdd,
  onEdit,
  onDelete,
  onClearError,
}: ManufacturerListProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'manufacturerName', desc: false },
    { id: 'modelName', desc: false },
  ]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dcFilter, setDcFilter] = useState<string>('all');
  const [ocppTariffFilter, setOcppTariffFilter] = useState<string>('all');
  const [ocppCostFilter, setOcppCostFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 50;

  // Filter manufacturers based on all filters
  const filteredManufacturers = useMemo(() => {
    return manufacturers.filter((m) => {
      // Search filter (manufacturer name or model name)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = m.manufacturerName.toLowerCase().includes(query);
        const matchesModel = m.modelName?.toLowerCase().includes(query) || false;
        if (!matchesName && !matchesModel) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (m.status !== Number(statusFilter)) return false;
      }

      // DC filter
      if (dcFilter !== 'all') {
        const isDcFilter = dcFilter === 'true';
        if (m.isDc !== isDcFilter) return false;
      }

      // OCPP Tariff filter
      if (ocppTariffFilter !== 'all') {
        const isOcppTariffFilter = ocppTariffFilter === 'true';
        if (m.isOcppTariff !== isOcppTariffFilter) return false;
      }

      // OCPP Cost filter
      if (ocppCostFilter !== 'all') {
        const isOcppCostFilter = ocppCostFilter === 'true';
        if (m.isOcppCost !== isOcppCostFilter) return false;
      }

      return true;
    });
  }, [manufacturers, searchQuery, statusFilter, dcFilter, ocppTariffFilter, ocppCostFilter]);

  // Pagination calculations
  const totalFilteredCount = filteredManufacturers.length;
  const totalPages = Math.ceil(totalFilteredCount / pageSize);
  const startItem = totalFilteredCount > 0 ? currentPage * pageSize + 1 : 0;
  const endItem = Math.min((currentPage + 1) * pageSize, totalFilteredCount);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  // Paginated data for display
  const paginatedManufacturers = useMemo(() => {
    const start = currentPage * pageSize;
    return filteredManufacturers.slice(start, start + pageSize);
  }, [filteredManufacturers, currentPage, pageSize]);

  const hasActiveFilters = statusFilter !== 'all' || dcFilter !== 'all' || ocppTariffFilter !== 'all' || ocppCostFilter !== 'all';

  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDcFilter('all');
    setOcppTariffFilter('all');
    setOcppCostFilter('all');
    setCurrentPage(0);
  };

  // Reset to first page when filters change
  const handleFilterChange = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setCurrentPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(0);
  };

  const columns = useMemo<ColumnDef<ManufacturerItem>[]>(
    () => [
      {
        accessorKey: 'id',
        header: ({ column }) => <SortableHeader column={column} label="ID" />,
        cell: ({ getValue }) => (
          <span className="text-sm text-gray-500 font-mono">{getValue<number>()}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'manufacturerName',
        header: ({ column }) => <SortableHeader column={column} label="Manufacturer Name" />,
        cell: ({ getValue }) => (
          <span className="font-medium">{getValue<string>()}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: 'modelName',
        header: ({ column }) => <SortableHeader column={column} label="Model Name" />,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.modelName;
          const b = rowB.original.modelName;
          // Nulls first
          if (a === null && b === null) return 0;
          if (a === null) return -1;
          if (b === null) return 1;
          return a.localeCompare(b);
        },
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? (
            <span className="text-sm">{value}</span>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <SortableHeader column={column} label="Status" />,
        cell: ({ getValue }) => {
          const status = getValue<number>();
          const isActive = status === ManufacturerStatus.Active;
          return (
            <span className={isActive ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
              {ManufacturerStatusLabels[status as ManufacturerStatus] || 'Unknown'}
            </span>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'isDc',
        header: ({ column }) => <SortableHeader column={column} label="AC/DC" />,
        cell: ({ getValue }) => {
          const value = getValue<boolean>();
          return (
            <div className="flex justify-center">
              <span className={value ? 'font-medium text-blue-600' : 'text-gray-600'}>
                {value ? 'DC' : 'AC'}
              </span>
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'isOcppTariff',
        header: ({ column }) => <SortableHeader column={column} label="OCPP Tariff" />,
        cell: ({ getValue }) => {
          const value = getValue<boolean>();
          return (
            <div className="flex justify-center">
              {value ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-gray-300" />
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: 'isOcppCost',
        header: ({ column }) => <SortableHeader column={column} label="OCPP Cost" />,
        cell: ({ getValue }) => {
          const value = getValue<boolean>();
          return (
            <div className="flex justify-center">
              {value ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-gray-300" />
              )}
            </div>
          );
        },
        enableSorting: true,
      },
      {
        id: 'createdDate',
        header: ({ column }) => <SortableHeader column={column} label="Created Date" />,
        accessorFn: (row) => new Date(row.created).getTime(),
        cell: ({ row }) => (
          <span className="text-xs text-gray-600">{formatDateTime(row.original.created)}</span>
        ),
        enableSorting: true,
      },
      {
        id: 'createdBy',
        header: 'Created By',
        cell: ({ row }) => (
          <span className="text-xs text-gray-600">{row.original.createdByName || '-'}</span>
        ),
      },
      {
        id: 'updatedDate',
        header: ({ column }) => <SortableHeader column={column} label="Updated Date" />,
        accessorFn: (row) => new Date(row.updated).getTime(),
        cell: ({ row }) => (
          <span className="text-xs text-gray-600">{formatDateTime(row.original.updated)}</span>
        ),
        enableSorting: true,
      },
      {
        id: 'updatedBy',
        header: 'Updated By',
        cell: ({ row }) => (
          <span className="text-xs text-gray-600">{row.original.updatedByName || '-'}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const manufacturer = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(manufacturer);
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(manufacturer);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [onEdit, onDelete]
  );

  const table = useReactTable({
    data: paginatedManufacturers,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Loading State
  if (isLoading && manufacturers.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-64" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <div className="rounded-md border border-[#E8E8E8] overflow-hidden">
          <table className="w-full">
            <thead className={GRID_STYLES.header.background}>
              <tr>
                {Array.from({ length: 8 }).map((_, i) => (
                  <th key={i} className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}>
                    <Skeleton className="h-4 w-20" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className={cn(GRID_STYLES.row.default, `border-b ${GRID_STYLES.border}`)}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className={GRID_STYLES.cell.base}>
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error loading manufacturers</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="flex gap-2">
          <Button onClick={onRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
          <Button variant="outline" onClick={onClearError}>
            Dismiss
          </Button>
        </div>
      </div>
    );
  }

  // Empty State
  if (!isLoading && manufacturers.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Manufacturers</h2>
          <Button onClick={onAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add New Manufacturer
          </Button>
        </div>
        <div className="border rounded-md">
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="bg-gray-100 rounded-full p-4 mb-4">
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Manufacturers Found</h3>
            <p className="text-gray-600 mb-6 max-w-md">
              Get started by adding your first manufacturer to the system.
            </p>
            <Button onClick={onAdd}>
              <Plus className="mr-2 h-4 w-4" />
              Add New Manufacturer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Data Table
  return (
    <div className="flex flex-col h-full">
      {/* Header with yellow accent border */}
      <div className="flex-shrink-0 border-l-4 border-yellow-400 pl-4 mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Manufacturers ({totalCount})
        </h2>
      </div>

      {/* Controls row */}
      <div className="flex-shrink-0 flex flex-col gap-4 mb-4">
        {/* Filter row + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3">
            {/* Status Filter */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Status</span>
              <Select value={statusFilter} onValueChange={handleFilterChange(setStatusFilter)}>
                <SelectTrigger className="w-[100px] h-9 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="1">Active</SelectItem>
                  <SelectItem value="0">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* DC/AC Filter */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Type</span>
              <Select value={dcFilter} onValueChange={handleFilterChange(setDcFilter)}>
                <SelectTrigger className="w-[90px] h-9 text-sm">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">DC</SelectItem>
                  <SelectItem value="false">AC</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* OCPP Tariff Filter */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">OCPP Tariff</span>
              <Select value={ocppTariffFilter} onValueChange={handleFilterChange(setOcppTariffFilter)}>
                <SelectTrigger className="w-[90px] h-9 text-sm">
                  <SelectValue placeholder="OCPP Tariff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* OCPP Cost Filter */}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">OCPP Cost</span>
              <Select value={ocppCostFilter} onValueChange={handleFilterChange(setOcppCostFilter)}>
                <SelectTrigger className="w-[90px] h-9 text-sm">
                  <SelectValue placeholder="OCPP Cost" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Yes</SelectItem>
                  <SelectItem value="false">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-9 px-2 text-sm text-gray-500 hover:text-gray-700"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by Manufacturer or Model..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            {/* Export Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportManufacturersToExcel(filteredManufacturers)}
              className="h-9 px-3 border-[#E8E8E8] text-[#262626] hover:bg-[#FFFBEB]"
              disabled={isLoading || filteredManufacturers.length === 0}
            >
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
            <Button variant="outline" size="icon" onClick={onRefresh} disabled={isLoading} className="h-9 w-9">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={onAdd} className="h-9">
              <Plus className="mr-2 h-4 w-4" />
              Add New
            </Button>
          </div>
        </div>
      </div>

      {/* Table - Scrollable area */}
      <div className="flex-1 min-h-0 overflow-auto rounded-md border border-[#E8E8E8]">
        <table className="w-full">
          <thead className={cn(GRID_STYLES.header.background, 'sticky top-0 z-10')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(GRID_STYLES.header.cell, GRID_STYLES.header.text)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    GRID_STYLES.row.default,
                    GRID_STYLES.row.hover,
                    `border-b ${GRID_STYLES.border}`,
                    'transition-colors cursor-pointer'
                  )}
                  onDoubleClick={() => onEdit(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={GRID_STYLES.cell.base}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="h-24 text-center text-gray-500">
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - Fixed at bottom */}
      <div className={cn('flex-shrink-0 flex items-center justify-between px-2 py-3 border-x border-b bg-white', GRID_STYLES.border)}>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {getPageNumbers().map((pageNum) => (
            <Button
              key={pageNum}
              variant={currentPage === pageNum ? 'default' : 'ghost'}
              size="sm"
              className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]' : 'text-[#262626] hover:bg-[#FFFBEB]'}`}
              onClick={() => setCurrentPage(pageNum)}
            >
              {pageNum + 1}
            </Button>
          ))}
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages - 1 || totalFilteredCount === 0}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1 || totalFilteredCount === 0}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
          <span className="text-sm text-[#606060] ml-2">{currentPage + 1} of {totalPages || 1} pages</span>
        </div>
        <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalFilteredCount.toLocaleString()}</span>
      </div>
    </div>
  );
}
